import { useCallback, useEffect, useState } from 'react'
import {
  confirmCollected,
  loadAwaitingCollection,
  type AwaitingCollection,
} from '@core/collections'
import { formatCents } from '@core/money'
import { Card, Section } from '@design/components'

/**
 * "Is dit afgeschreven?"
 *
 * Two buttons, no form, and both answers are useful. Ja settles it; Nee turns
 * it into something that needs attention, which is exactly what a failed
 * collection is and exactly what nothing else in the app would ever notice.
 *
 * Hidden entirely when there is nothing to confirm (rule 23) — which is most
 * days, since a direct debit that worked answers its own question the moment
 * you say so once.
 */
export function CollectionPrompt({ userId }: { userId: string }) {
  const [items, setItems] = useState<AwaitingCollection[]>([])
  const [busy, setBusy] = useState<string | null>(null)

  const refresh = useCallback(() => {
    loadAwaitingCollection(userId)
      .then(setItems)
      .catch(() => setItems([]))
  }, [userId])

  useEffect(refresh, [refresh])

  if (items.length === 0) return null

  async function answer(item: AwaitingCollection, collected: boolean) {
    setBusy(item.instalmentId)
    try {
      await confirmCollected(item, collected, userId)
      refresh()
    } finally {
      setBusy(null)
    }
  }

  return (
    <Section title="Afgeschreven?" count={items.length}>
      <div className="space-y-2">
        {items.map((item) => (
          <Card key={item.instalmentId}>
            <div className="flex items-baseline justify-between gap-2">
              <p className="font-medium truncate">{item.description}</p>
              <span className="tabular-nums shrink-0">{formatCents(item.amountCents)}</span>
            </div>
            <p className="text-sm text-muted mb-2">
              Stond op {formatDate(item.dueDate)} — is het van je rekening gegaan?
            </p>
            <div className="flex gap-2">
              <button
                disabled={busy === item.instalmentId}
                onClick={() => answer(item, true)}
                className="flex-1 min-h-[2.75rem] rounded-xl border border-line font-medium active:bg-canvas"
              >
                Ja
              </button>
              <button
                disabled={busy === item.instalmentId}
                onClick={() => answer(item, false)}
                className="flex-1 min-h-[2.75rem] rounded-xl border border-warn/50 text-warn font-medium"
              >
                Nee
              </button>
            </div>
          </Card>
        ))}
      </div>
    </Section>
  )
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('nl-BE', { day: 'numeric', month: 'long' }).format(
    new Date(`${iso}T00:00:00`),
  )
}
